/**
 * Training hub (LCC / lms-gated) — integrates training scheduling, attendance
 * and certification into one sidebar page with internal tabs.
 * Attendance/RSVP is built into the Training Schedule page, so it lives under
 * the "Schedule & Attendance" tab.
 * Tabs: Catalogue · Compliance · Reports (TRUS-486 staff training program)
 * then Cohorts · Schedule & Attendance · Records & Certifications.
 * Records and Certifications were separate tabs until TRUS-562 merged them:
 * they answer one question split only by where the training happened.
 */
import React from 'react';
import { useCapability } from '@/hooks/useCapabilities';
import { CalendarDays, Award, Users, BookOpen, ShieldCheck, BarChart3 } from 'lucide-react';
import HubShell from './HubShell';
import { PAGE_SWITCH, usePageSwitch } from './usePageSwitch';
import WorkforceTrainingSchedule from '../training-schedule/page';
import CohortsPage from '../cohorts/page';
import CatalogPage from '../training-program/CatalogPage';
import RecordsAndCertificationsPage from '../training-program/RecordsAndCertificationsPage';
import CompliancePage from '../training-program/CompliancePage';
import ReportsPage from '../training-program/ReportsPage';

export const TRAINING_HUB_PAGE_KEY = 'workforce-training-hub';

export default function TrainingHubPage() {
  // The staff training program is gated on its own capability, not on `lms`. Both are
  // held by Amigos, but `lms` is ALSO held by Latinas Contra Cancer, who do not run this
  // program — gating on it would have put four unexplained empty tabs in front of them.
  // The hook defaults to disabled, so a missing capability hides the tabs rather than
  // erroring.
  const orgId = Number(localStorage.getItem('active_cbo_id') || 0) || null;
  const { enabled: staffTraining } = useCapability(orgId, 'staff_training');
  // TRUS-562: Cohorts and Schedule & Attendance are the LMS's (member cohorts and
  // classroom sessions) and follow `lms`. LCC holds it and sees no change.
  const { enabled: lms } = useCapability(orgId, 'lms');

  // TRUS-562: each staff-training tab can be switched off per role in the Permission
  // Matrix. The switches only hide, and do nothing for an org without staff_training.
  const catalogueOff = usePageSwitch(orgId, PAGE_SWITCH.catalogue).switchedOff;
  const complianceOff = usePageSwitch(orgId, PAGE_SWITCH.compliance).switchedOff;
  const reportsOff = usePageSwitch(orgId, PAGE_SWITCH.reports).switchedOff;
  const recordsOff = usePageSwitch(orgId, PAGE_SWITCH.records).switchedOff;

  const trainingProgramTabs = staffTraining
    ? [
        ...(catalogueOff
          ? []
          : [{ key: 'catalogue', label: 'Catalogue', icon: BookOpen, render: () => <CatalogPage /> }]),
        ...(complianceOff
          ? []
          : [{ key: 'compliance', label: 'Compliance', icon: ShieldCheck, render: () => <CompliancePage /> }]),
        ...(reportsOff ? [] : [{ key: 'reports', label: 'Reports', icon: BarChart3, render: () => <ReportsPage /> }]),
      ]
    : [];

  return (
    <HubShell
      pageKey={TRAINING_HUB_PAGE_KEY}
      title="Training"
      subtitle="Define trainings, record who completed them, track compliance, and manage sessions and certifications."
      tabs={[
        ...trainingProgramTabs,
        ...(lms
          ? [
              {
                key: 'cohorts',
                label: 'Cohorts',
                icon: Users,
                render: () => <CohortsPage />,
              },
              {
                key: 'schedule',
                label: 'Schedule & Attendance',
                icon: CalendarDays,
                render: () => <WorkforceTrainingSchedule />,
              },
            ]
          : []),
        ...(recordsOff
          ? []
          : [
              {
                // TRUS-562: one tab, two sections. Sits outside the staff_training
                // group because it must also appear for an org that only has the
                // certifications half — gating it on staff_training would have taken
                // Certifications away from LCC. The records section re-checks that
                // capability itself.
                key: 'records-certifications',
                label: 'Records & Certifications',
                icon: Award,
                render: () => <RecordsAndCertificationsPage />,
              },
            ]),
      ]}
    />
  );
}
